import { useCallback, useEffect, useState } from 'react';
import { InvoiceDetailModel } from '@frontend/api-invoices';
import { PaymentMethod } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { CheckboxField, Modal, Text, useAlert, useFormField, useModalControl } from '@frontend/design-system';

export type UseInvoiceSurchargeConfirmModalProps = {
  invoice: InvoiceDetailModel | undefined;
  paymentMethod: PaymentMethod | undefined;

  /**
   * Use if you have additional checks.
   * Return true if you want the flow to proceed, false if you want to abort.
   */
  onConfirm: () => Promise<void>;
};

const styles = {
  marginBottom: {
    marginBottom: theme.spacing(1),
  },
};
export const useInvoiceSurchargeConfirmModal = ({
  invoice,
  paymentMethod,
  onConfirm,
}: UseInvoiceSurchargeConfirmModalProps) => {
  const { t } = useTranslation();
  const alerts = useAlert();
  const { modalProps, openModal, closeModal } = useModalControl();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const agreeField = useFormField({ type: 'checkbox', value: false });

  const isConfirmRequired = useCallback(() => {
    const hasSurchargeEnabled = invoice?.surchargingEnabled;

    const isCreditFunding = paymentMethod?.type === 'card' && paymentMethod?.card?.funding === 'credit';

    return hasSurchargeEnabled && isCreditFunding;
  }, [invoice, paymentMethod]);

  const handleClickConfirm = useCallback(async () => {
    if (!agreeField.value) {
      alerts.error(t('You must agree to the terms before proceeding.'));
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm();
    } catch (err) {
      console.error(err);
    }
    closeModal();
    setIsSubmitting(false);
  }, [onConfirm, closeModal]);

  const handleClickCancel = useCallback(async () => {
    closeModal();
  }, [closeModal]);

  useEffect(() => {
    /**
     * Uncheck the agree box when modal is hidden.
     * Is closed from outside, so wrapping the modalProps.onClose here doesn't do it.
     */
    if (!modalProps.show) {
      agreeField.onChange({ name: 'agree', value: false });
    }
  }, [modalProps.show]);

  const ConfirmModal = (
    <Modal {...modalProps} minWidth={600}>
      <Modal.Header>{t('Agree to Terms')}</Modal.Header>
      <Modal.Body>
        <Text css={styles.marginBottom}>
          {t('This charge will include a surcharge. Please notify the customer of this...')}
        </Text>

        <div>
          <CheckboxField {...agreeField} name='agree' label={t('I agree')} />
        </div>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Complete with Surcharge'),
          onClick: handleClickConfirm,
          disabled: isSubmitting || !agreeField.value || !invoice?.id,
          loading: isSubmitting,
        }}
        tertiary={{
          label: t('Cancel'),
          disabled: isSubmitting,
          onClick: handleClickCancel,
        }}
      ></Modal.Footer>
    </Modal>
  );

  return {
    isConfirmRequired,
    modalProps,
    openModal,
    closeModal,
    ConfirmModal,
  };
};
